/**
 * useViewPerCurrency Hook
 *
 * A selector-based utility hook that extracts column-specific values from
 * the `viewPerCurrencyData` slice of the stats store. It is used primarily by:
 *
 *   • ViewPerCurrency footer component
 *   • ExpandSum footer component
 *   • Any part of the UI that needs column-by-column currency-grouped values
 *
 * ----------------------------------------------------------------------------------
 * Data Source:
 * ----------------------------------------------------------------------------------
 *   state.stats.viewPerCurrencyData.Items
 *     → An array of aggregated values grouped by currency.
 *
 *   Each object inside Items typically has the shape:
 *     {
 *       CCY,
 *       Bet,
 *       BetTax,
 *       BetAfterTax,
 *       Payout,
 *       PayoutTax,
 *       PayoutAfterTax,
 *       Win,
 *       WinTax,
 *       WinAfterTax,
 *       ForPayout,
 *       ForPayoutTax,
 *       ForPayoutAfterTax,
 *       GGR,
 *       Balance,
 *       Percentage,
 *       AverageStake,
 *       NumberOfPayInTickets,
 *       NumberOfPayoutTickets,
 *       NumberOfDeletedTickets,
 *       NumberOfTickets
 *     }
 *
 * ----------------------------------------------------------------------------------
 * Parameters:
 * ----------------------------------------------------------------------------------
 *   column (string):
 *     A StatsTableColumn enum value.
 *     Determines which field should be extracted from each item.
 *
 * ----------------------------------------------------------------------------------
 * Returns:
 * ----------------------------------------------------------------------------------
 *   string[] | number[]
 *
 *   Always returns an array (empty if:
 *      • Items is empty
 *      • Column is unrecognized)
 *
 * ----------------------------------------------------------------------------------
 * Behavior:
 * ----------------------------------------------------------------------------------
 *   1. Reads `viewPerCurrencyData.Items` from Redux using useAppSelector.
 *   2. If Items is empty → returns an empty array immediately.
 *   3. Switches on `column`, mapping each item to the appropriate field.
 *   4. Returns the complete array used for rendering column values in the footer.
 *
 * ----------------------------------------------------------------------------------
 * Example Usage:
 * ----------------------------------------------------------------------------------
 *
 *   const values = useViewPerCurrency(StatsTableColumn.BET);
 *   -> [1200, 800, 450, ...]
 *
 *   const currencies = useViewPerCurrency(StatsTableColumn.CCY);
 *   -> ["EUR", "USD", "GBP", ...]
 *
 */

import { useAppSelector } from './useAppSelector';
import { StatsTableColumn } from '../enum/TableColumn';

export const useViewPerCurrency = (column: string) => {
  const viewPerCurrencyData = useAppSelector((state) => state.stats.viewPerCurrencyData);
  let data: string[] | number[] = [];

  if (viewPerCurrencyData.Items.length === 0) return data;

  switch (column) {
    case StatsTableColumn.CCY:
      data = viewPerCurrencyData.Items.map((item) => item.CCY);
      break;

    case StatsTableColumn.BET:
      data = viewPerCurrencyData.Items.map((item) => item.Bet);
      break;

    case StatsTableColumn.BET_TAX:
      data = viewPerCurrencyData.Items.map((item) => item.BetTax);
      break;

    case StatsTableColumn.BET_AFTER_TAX:
      data = viewPerCurrencyData.Items.map((item) => item.BetAfterTax);
      break;

    case StatsTableColumn.PAYOUT:
      data = viewPerCurrencyData.Items.map((item) => item.PayOut);
      break;

    case StatsTableColumn.PAYOUT_TAX:
      data = viewPerCurrencyData.Items.map((item) => item.PayoutTax);
      break;

    case StatsTableColumn.PAYOUT_AFTER_TAX:
      data = viewPerCurrencyData.Items.map((item) => item.PayOutAfterTax);
      break;

    case StatsTableColumn.WIN:
      data = viewPerCurrencyData.Items.map((item) => item.Win);
      break;

    case StatsTableColumn.WIN_TAX:
      data = viewPerCurrencyData.Items.map((item) => item.WinTax);
      break;

    case StatsTableColumn.WIN_AFTER_TAX:
      data = viewPerCurrencyData.Items.map((item) => item.WinAfterTax);
      break;

    case StatsTableColumn.FOR_PAYOUT:
      data = viewPerCurrencyData.Items.map((item) => item.ForPayout);
      break;

    case StatsTableColumn.FOR_PAYOUT_TAX:
      data = viewPerCurrencyData.Items.map((item) => item.ForPayoutTax);
      break;

    case StatsTableColumn.FOR_PAYOUT_AFTER_TAX:
      data = viewPerCurrencyData.Items.map((item) => item.ForPayoutAfterTax);
      break;

    case StatsTableColumn.GGR:
      data = viewPerCurrencyData.Items.map((item) => item.GGR);
      break;

    case StatsTableColumn.BALANCE:
      data = viewPerCurrencyData.Items.map((item) => item.Balance);
      break;

    case StatsTableColumn.PERCENTAGE:
      data = viewPerCurrencyData.Items.map((item) => item.Percentage);
      break;

    case StatsTableColumn.AVERAGE_STAKE:
      data = viewPerCurrencyData.Items.map((item) => item.AverageStake);
      break;

    case StatsTableColumn.NUMBER_OF_PAY_IN_TICKETS:
      data = viewPerCurrencyData.Items.map((item) => item.NumberOfPayInTickets);
      break;

    case StatsTableColumn.NUMBER_OF_PAY_OUT_TICKETS:
      data = viewPerCurrencyData.Items.map((item) => item.NumberOfPayOutTickets);
      break;

    case StatsTableColumn.NUMBER_OF_DELETED_TICKETS:
      data = viewPerCurrencyData.Items.map((item) => item.NumberOfDeletedTickets);
      break;

    case StatsTableColumn.NUMBER_OF_TICKETS:
      data = viewPerCurrencyData.Items.map((item) => item.NumberOfTickets);
      break;
  }

  return data;
};
